import { format } from "date-fns";

import { IClassroomResource } from "../../classrooms";
import { IFrameworkResource } from "../../frameworks";
import { ILanguageResource } from "../../languages";
import { ISkillLevelResource } from "../../skill-levels";
import { ISkillResource } from "../../skills";
import { ITagResource } from "../../tags";
import { IUserResource } from "../../users";
import {
    createApiQueryHook,
    IApiQueryFunction,
    IOrderable,
    IPaginateable,
    mapOrder,
    mapPagination,
} from "../../utils/api";
import {
    mapRelationalFilter,
    mapRelationalValue,
    OneOrManyFilter,
    RelationalFilter,
    RelationalValue,
} from "../../utils/filters";
import { serialize } from "../../utils/url";
import { AssignationType, BriefStatus, CorrectionStatus, IBriefList } from "../types";

export type BriefOrderBy = "updatedAt" | "createdAt" | "title";
export type BriefOrder = "asc" | "desc";

export interface IBriefListQueryParameters extends IPaginateable, IOrderable<"title" | "createdAt" | "updatedAt"> {
    filters?: {
        activeClassroom?: RelationalValue<IClassroomResource>;
        classrooms?: RelationalFilter<IClassroomResource>;
        correctionStatus?: OneOrManyFilter<CorrectionStatus>;
        coeditors?: RelationalFilter<IUserResource>;
        createdByConnectedUser?: boolean;
        createdBy?: RelationalFilter<IUserResource>;
        hasAssignedClassrooms?: boolean;
        creatorEmail?: string;
        ownerAndCoeditor?: boolean;
        status?: OneOrManyFilter<BriefStatus>;
        assignationType?: OneOrManyFilter<AssignationType>;
        title?: string | null;

        assignedClassroom?: RelationalValue<IClassroomResource>;
        assignedLearner?: RelationalValue<IUserResource>;

        topicClassrooms?: RelationalFilter<IClassroomResource>;
        topicLearners?: RelationalFilter<IUserResource>;

        frameworks?: RelationalFilter<IFrameworkResource>;
        skills?: RelationalFilter<ISkillResource>;
        skillLevels?: RelationalFilter<ISkillLevelResource>;

        tags?: RelationalFilter<ITagResource>;
        languages?: RelationalFilter<ILanguageResource>;
        correctionStart?: string;
        correctionEnd?: string;
    };
}

export type IBriefListQueryResult = IBriefList;

const mapFilters = (filters: IBriefListQueryParameters["filters"]) => {
    const {
        activeClassroom,
        classrooms,
        correctionStatus,
        topicClassrooms,
        topicLearners,
        coeditors,
        createdByConnectedUser,
        createdBy,
        creatorEmail,
        hasAssignedClassrooms,
        status,
        assignationType,
        title,
        assignedClassroom,
        assignedLearner,
        frameworks,
        skills,
        skillLevels,
        tags,
        languages,
        ownerAndCoeditor,
        correctionStart,
        correctionEnd,
    } = filters || {};

    // Format correction dates if they are present
    const formattedCorrectionStart = correctionStart ? format(new Date(correctionStart), "yyyy-MM-dd") : undefined;
    const formattedCorrectionEnd = correctionEnd ? format(new Date(correctionEnd), "yyyy-MM-dd") : undefined;

    return {
        "activeClassroom.uuid": mapRelationalValue(activeClassroom),
        "classrooms.uuid": mapRelationalFilter(classrooms),
        "coeditors.uuid": mapRelationalFilter(coeditors),
        "createdBy.uuid": mapRelationalFilter(createdBy),
        "createdBy.email": creatorEmail,
        "exists[classrooms]": hasAssignedClassrooms,

        createdByConnectedUser,
        status,
        assignationType,
        correctionStatus,
        title,
        ownerAndCoeditor,
        correctionStart: formattedCorrectionStart,
        correctionEnd: formattedCorrectionEnd,

        "assigned.classroom.uuid": mapRelationalValue(assignedClassroom),
        "assigned.learner.uuid": mapRelationalValue(assignedLearner),

        /** Seems no more in use across the app: perhaps can we delete these 2 filters in the API */
        "topics.classroom.uuid": mapRelationalFilter(topicClassrooms),
        "topics.learner.uuid": mapRelationalFilter(topicLearners),

        "skillLevels.skill.frameworks.uuid": mapRelationalFilter(frameworks),
        "skillLevels.skill.uuid": mapRelationalFilter(skills),
        "skillLevels.uuid": mapRelationalFilter(skillLevels),

        "tags.uuid": mapRelationalFilter(tags),
        "language.uuid": mapRelationalFilter(languages),
    };
};

export const briefListQuery: IApiQueryFunction<IBriefListQueryParameters> = ({ filters, pagination, order }) => {
    return {
        method: "GET",
        url: `/briefs${serialize(
            {
                ...mapPagination(pagination),
                ...mapOrder(order),
                ...mapFilters(filters),
            },
            true,
        )}`,
    };
};

export const useBriefListQuery = createApiQueryHook<IBriefListQueryParameters, IBriefListQueryResult>(briefListQuery);
